import React, { useEffect, useState } from "react"
import { SpaceUpdateForm } from "../component/space/SpaceUpdateForm"
import { SpaceUserForm } from "../component/space/SpaceUserForm"
import { api, firestore, storage } from "../core"
import { Link, Navigate } from "react-router-dom"
import { SpaceBackgroundId, SpaceBGMId } from "popopo-msg"
import { SpaceUserSelfForm } from "../component/space/SpaceUserSelfForm"
import { useSpace, useSpaceBackground, useSpaceBGM, useLatestSpaceInvite, useSpaceUsers, useUserSpacePushSetting } from "popopo-lib-front/firestore-access"
import { useAccessContext } from "../component/guard/AccessGuard"
import { SpaceConnectionForm } from "../component/space/SpaceConnectionForm"
import { SpaceLiveList } from "../component/live/SpaceLiveList"
import { SpaceLiveCreateForm } from "../component/live/SpaceLiveCreateForm"
import { SpaceRoleNormalSwitchButton } from "../component/space/SpaceRoleNormalSwitchButton"
import { SpaceRoleDebugSwitchButton } from "../component/space/SpaceRoleDebugSwitchButton"
import { SpaceMessageCreateForm } from "../component/space/SpaceMessageCreateForm"
import { SpaceHiddenViewerForm } from "../component/space/SpaceHiddenViewerForm"
import { SpaceMessageList } from "../component/space/SpaceMessageList"
import { SpaceBGMUpdateForm } from "../component/space/SpaceBGMUpdateForm"
import { SpaceBackgroundUpdateForm } from "../component/space/SpaceBackgroundUpdateForm"
import { getDownloadURL, ref } from "firebase/storage"
import { SpinnerRow } from "../component/SpinnerRow"
import { SpaceFriendsCallCreateForm } from "../component/space/SpaceFriendsCallCreateForm"
import { SpaceMutedForm } from "../component/space/SpaceMutedForm"
import { Spinner } from "react-bootstrap"
import ChangeSpacePushSettingButton from "../component/user/ChangeSpacePushSettingButton"
import { CreateInviteForm } from "../component/CreateInviteForm"
import { Paths } from "../paths"
import { usePathParams } from "popopo-lib-front/hook/path"
import { ShareImageDownloadButton } from "../component/ShareImageDownloadButton"

export const Space = () => {
  const context = useAccessContext()
  const { spaceKey } = usePathParams(Paths.spaces.space)
  const space = useSpace(firestore, spaceKey)
  const spaceUsers = useSpaceUsers(firestore, spaceKey)
  const spaceBackground = useSpaceBackground(
    firestore,
    space !== "loading" && space && space.background?.kind === "shared"
      ? space.background.id
      : SpaceBackgroundId("")
  )
  const spaceBGM = useSpaceBGM(
    firestore,
    space !== "loading" && space && space.bgm?.kind === "shared" && space.bgm.id
      ? space.bgm.id
      : SpaceBGMId("")
  )
  const userSpacePushSetting = useUserSpacePushSetting(firestore, context.user.id, spaceKey)
  const [spaceBackgroundURL, setSpaceBackgroundURL] = useState<string | null>(null)
  const [spaceBGMURL, setSpaceBGMURL] = useState<string | null>(null)

  useEffect(() => {
    if (spaceBackground !== "loading" && spaceBackground) {
      void getDownloadURL(ref(storage, spaceBackground.thumbnail_url)).then(url =>
        setSpaceBackgroundURL(url)
      )
    }
  }, [spaceBackground])

  useEffect(() => {
    if (spaceBGM !== "loading" && spaceBGM) {
      void getDownloadURL(ref(storage, spaceBGM.url)).then(url =>
        setSpaceBGMURL(url)
      )
    }
  }, [spaceBGM])

  const invite = useLatestSpaceInvite(firestore, spaceKey)

  if (space === "loading" || spaceUsers === "loading") {
    return <SpinnerRow />
  }

  if (space === null) {
    return <Navigate to={Paths.top.path} />
  }

  return (<>
    <div className="row">
      <div className="col-12 offset-xl-1 col-xl-10 d-flex flex-column align-items-center gap-3">
        <h3>Space Info</h3>
        <div>
          <div>Name: {space.name}</div>
          <div>Owner: {space.user_id}</div>
          <div>Live: {space.live_id ?? "None"}</div>
          {space.background
            ? <div>背景： ID={space.background.id}, kind={space.background.kind}</div>
            : <div>背景なし</div>
          }
          {(!space.bgm || !space.bgm.id) ? (
            <div>BGM: 設定なし</div>
          ) : (
            <>
              <div>BGM ID: {space.bgm.id}, kind={space.bgm.kind}, volume={space.bgm.volume}</div>
            </>
          )}
        </div>

        <h3>Space Background</h3>
        <div>
          {spaceBackground === "loading"
            ? <p>背景読込中</p>
            : spaceBackground === null
              ? <p className="text-danger">背景が存在しません(データ不整合の可能性あり)</p>
              : <div>
                <div>RecommendedBGM: {spaceBackground.recommend_bgm}</div>
                <div>Name: {spaceBackground.name}</div>
                <div>Capacity: {spaceBackground.capacity}</div>
                <div>Atmosphere Text: {spaceBackground.atmosphere_text}</div>
                <div>Status: {spaceBackground.status}</div>
                <div>CanDelete: {spaceBackground.can_delete ? "true" : "false"}</div>
                {spaceBackgroundURL && (
                  <div>
                    <img alt={"背景"} width={320} src={spaceBackgroundURL} />
                  </div>
                )}
              </div>
          }
        </div>

        <h3>Space BGM</h3>
        <div>
          {space.bgm === null
            ? <p>BGMが設定されていません</p>
            : spaceBGM === "loading"
              ? <p>BGM情報読込中</p>
              : spaceBGM === null
                ? <p className="text-danger">BGMが存在しません(データ不整合の可能性あり)</p>
                : <div>
                  <div>Name: {spaceBGM.name}</div>
                  <div>Status: {spaceBGM.status}</div>
                  <div>CanDelete: {spaceBGM.can_delete ? "true" : "false"}</div>
                  {spaceBGMURL && <div>
                    <audio src={spaceBGMURL} controls />
                  </div>}
                </div>
          }
        </div>

        <h3>Space User Info</h3>
        <table className="table">
          <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">Muted</th>
            <th scope="col">Last joined at</th>
            <th scope="col">Last connected at</th>
            <th scope="col">Created at</th>
            <th scope="col">Updated at</th>
            <th scope="col">Debug Action</th>
          </tr>
          </thead>
          <tbody>
          {spaceUsers.map((spaceUser) => (
            <tr className={spaceUser.user_id === context.user.id ? "table-success" : ""} key={spaceUser.id}>
              <td style={{ wordBreak: "break-word" }}>(<Link to={Paths.debug.users.user.getPath(spaceUser.user_id)}>{spaceUser.user?.name ?? "削除済み"}</Link>) <code>{spaceUser.user_id}</code></td>
              <td><SpaceRoleNormalSwitchButton firebase={context.credential.user} spaceUser={spaceUser} /></td>
              <td>{spaceUser.status}</td>
              <td>{String(spaceUser.muted)}</td>
              <td>{spaceUser.last_joined_at ? new Date(spaceUser.last_joined_at).toLocaleString() : "未接続"}</td>
              <td>{spaceUser.last_connected_at ? new Date(spaceUser.last_connected_at).toLocaleString() : "未接続"}</td>
              <td>{new Date(spaceUser.created_at).toLocaleString()}</td>
              <td>{new Date(spaceUser.updated_at).toLocaleString()}</td>
              <td>
                <SpaceRoleDebugSwitchButton firebase={context.credential.user} spaceUser={spaceUser} />
              </td>
            </tr>
          ))}
          </tbody>
        </table>

        <h3>Space Selection Info</h3>
        <table className="table">
          <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Kind</th>
            <th scope="col">Status</th>
            <th scope="col">Title</th>
            <th scope="col">User ID</th>
            <th scope="col">Selected participant ID</th>
            <th scope="col">Selected user ID</th>
            <th scope="col">Selected user status</th>
            <th scope="col">Selected user muted</th>
            <th scope="col">Selected last joined at</th>
            <th scope="col">Selected last connected at</th>
          </tr>
          </thead>
          <tbody>
          {space.selections?.map((selection) => (
            <tr key={selection.id}>
              <td style={{ wordBreak: "break-word" }}>{selection.id}</td>
              <td>{selection.kind}</td>
              <td>{selection.status}</td>
              <td>{selection.title}</td>
              <td style={{ wordBreak: "break-word" }}>{selection.user_id}</td>
              {selection.selected_participant ? <>
                <td style={{ wordBreak: "break-word" }}>{selection.selected_participant.id}</td>
                <td style={{ wordBreak: "break-word" }}>{selection.selected_participant.user.id}</td>
                <td>{selection.selected_participant.status}</td>
                <td>{selection.selected_participant.muted}</td>
                <td>
                  {selection.selected_participant.last_joined_at
                    ? new Date(selection.selected_participant.last_joined_at).toLocaleString()
                    : "未入室"
                  }
                </td>
                <td>
                  {selection.selected_participant.last_connected_at
                    ? new Date(selection.selected_participant.last_connected_at).toLocaleString()
                    : "未入室"
                  }
                </td>
              </> : <>
                <td style={{ textAlign: "center" }} colSpan={6}><strong>未当選</strong></td>
              </>}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>

    <div className="row gy-5 justify-content-center">
      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Live</h3>
        <SpaceLiveList firebase={context.credential.user} spaceKey={space.id} />
        <SpaceLiveCreateForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Connect/Disconnect Space</h3>
        <SpaceConnectionForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Mute/Unmute</h3>
        <SpaceMutedForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Join/Leave Space</h3>
        <SpaceUserSelfForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Add/Remove Space</h3>
        <SpaceUserForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Space Invite</h3>
        {(context.user.id === space.user_id) && <>
          {invite === "loading"
            ? <Spinner />
            : invite === null
              ? <p>招待コードなし</p>
              : <div>
                <p>({invite.status}) <code>{invite.id}</code></p>
                <p>回数: {invite.count} / {invite.limit} </p>
                <p>有効時間:{new Date(invite.created_at).toLocaleString()} から {invite.expired_at_seconds}秒</p>
                <ShareImageDownloadButton
                  getIdToken={() => context.credential.user.getIdToken()}
                  path={`/api/v2/spaces/${spaceKey}/invites/${invite.id}/share-image`}
                  filename={`space-invite-${invite.id}-share-image.png`}
                  label="スペース招待共有画像DL"
                />
              </div>
          }</>
        }
        <CreateInviteForm onCreate={async (request) => {
          try {
            const idToken = await context.credential.user.getIdToken()
            const result = await api.POST("/api/v2/spaces/{space_key}/invites", {
              params: { path: { space_key: space.id } },
              headers: {
                authorization: `Bearer ${idToken}`,
              },
              body: request
            })
            if (!result.error) {
              const data = result.data
              return { error: null, createdInviteLink: "inviteLink" in data ? data.inviteLink : "", createdInviteKey: data.inviteKey }
            } else {
              return { error: result.error }
            }
          } catch (e) {
            console.error(e)
            return { error: e }
          }
        }} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Update Space</h3>
        <SpaceUpdateForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Update Space Background</h3>
        {space.background.kind === "inventory" ? (
          <p className="text-warning">現在インベントリアイテムが設定されています。管理画面では共有背景のみ設定可能です。</p>
        ) : (
          <SpaceBackgroundUpdateForm
            firebase={context.credential.user}
            spaceKey={space.id}
            backgroundConfiguration={space.background}
          />
        )}
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Update BGM</h3>
        {space.bgm && space.bgm.kind === "inventory" ? (
          <p className="text-warning">現在インベントリアイテムが設定されています。管理画面では共有BGMのみ設定可能です。</p>
        ) : (
          <SpaceBGMUpdateForm
            firebase={context.credential.user}
            spaceKey={space.id}
            bgmConfiguration={
              (!space.bgm || !space.bgm.id)
                ? null
                : {
                  defaultVolume: space.bgm.volume.toString(),
                  defaultBGMId: space.bgm.id,
                }
            }
          />
        )}
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Messages</h3>
        <SpaceMessageList firebase={context.credential.user} spaceKey={space.id} />

        <SpaceMessageCreateForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Ban Users</h3>
        <SpaceHiddenViewerForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Space Friends Call</h3>
        <SpaceFriendsCallCreateForm firebase={context.credential.user} spaceKey={space.id} />
      </div>

      <div className="col-6 col-xl-5 d-flex flex-column align-items-center gap-3">
        <h3>Space Push Setting</h3>
        {userSpacePushSetting === "loading" ? (<Spinner />) :
          <ChangeSpacePushSettingButton firebase={context.credential.user} spaceKey={space.id} newAccept={userSpacePushSetting?.accept_push === false} />
        }

      </div>
    </div>
  </>)
}
